import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/articles';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9oq2iqqs38sj6jc7dh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.51nav.com',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    foundingDate: siteInfo.founded,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51网址导航是正规平台吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，51网址导航是一个正规的上网导航平台，致力于为用户提供安全、便捷的网址导航服务。我们对接入的网站进行严格审核，确保用户的上网安全。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系51网址导航？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过邮箱contact@51nav.com或客服电话400-888-5151联系我们，也可以在本页面的联系表单中留言，我们会在24小时内回复。',
        },
      },
      {
        '@type': 'Question',
        name: '51网址导航有哪些导航服务？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51网址导航提供网址导航123快捷入口，集成了123导航、导航123等数字标识导航服务。您可以通过首页搜索框、常用网址快捷入口和分类导航等多种方式快速访问目标网站。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 51网址导航"
        description={`${siteInfo.description}了解51网址导航的品牌故事、发展历程和联系方式。网址123，易记好用的上网导航。`}
        keywords="关于我们, 51网址导航, 品牌介绍, 联系方式, 网址导航"
        canonicalUrl="/about"
        structuredData={organizationSchema}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-20 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4">
                <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
              </div>

              <section className="mb-12">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  关于51网址导航
                </h1>
                <p className="text-foreground-500 text-sm mb-6">
                  上网从这里开始
                </p>

                <div className="relative w-full h-56 md:h-72 rounded-xl overflow-hidden mb-8">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimal%20office%20workspace%20with%20warm%20lighting%2C%20creative%20team%20collaboration%20atmosphere%2C%20clean%20design%20aesthetic%2C%20wooden%20desk%20with%20computer%2C%20soft%20natural%20light%2C%20editorial%20style%2C%20orange%20and%20cream%20tones&width=1200&height=500&seq=about-hero-51nav&orientation=landscape"
                    alt="51网址导航团队"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
                  <p>
                    <strong>51网址导航</strong>成立于2020年，是一个面向广大网民的综合上网导航平台。
                    我们致力于为用户提供<strong>网址导航123</strong>快捷入口，集成<strong>123导航</strong>、
                    <strong>导航123</strong>等数字标识导航服务，打造<strong>网址123</strong>——易记好用的上网导航品牌。
                  </p>
                  <p>
                    在互联网信息爆炸的时代，记住所有常用网站的网址变得越来越困难。
                    51网址导航正是为了解决这一痛点而生。我们的域名简洁易记（51=我要），
                    配合数字标识"123"的导航理念，让用户只需记住一个网址就能快速访问所有常用网站。
                  </p>
                  <p>
                    除了网址导航功能，我们还提供<strong>热门资讯</strong>、<strong>吃瓜事件</strong>、
                    科技数码、生活百科、娱乐八卦、财经商业、教育学习、健康养生等多维度内容，
                    让用户在导航的同时可以浏览最新最热的网络资讯，实现一站式的上网体验。
                  </p>
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      name: '张先生',
                      role: '互联网从业者',
                      text: '51网址导航是我每天打开浏览器的第一站，网址123太好记了，分类清晰，找什么都很方便。推荐给了身边很多朋友，大家都说好用！',
                      rating: 5,
                    },
                    {
                      name: '李女士',
                      role: '大学生',
                      text: '最喜欢吃瓜事件栏目，更新快内容全。导航功能也很实用，常用的学习网站都收藏在这里了，再也不用一个一个记网址了。',
                      rating: 5,
                    },
                    {
                      name: '王老师',
                      role: '中学教师',
                      text: '作为不太懂电脑的人，51网址导航帮我解决了很多上网难题。界面简洁大方，搜索功能强大，资讯内容也很丰富。五星推荐！',
                      rating: 5,
                    },
                  ].map((review, idx) => (
                    <div
                      key={idx}
                      className="p-6 bg-background-50 rounded-xl border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-sm text-primary-500"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        &ldquo;{review.text}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full text-primary-600 font-semibold text-sm">
                          {review.name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                          <p className="text-xs text-foreground-500">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                          {siteInfo.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">客服电话</p>
                        <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">公司地址</p>
                        <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl text-primary-600 flex-shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">工作时间</p>
                        <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4 font-heading">
                      给我们留言
                    </h3>
                    {formStatus === 'success' ? (
                      <div className="text-center py-8">
                        <div className="w-14 h-14 flex items-center justify-center bg-secondary-100 rounded-full mx-auto mb-3">
                          <i className="ri-check-line text-2xl text-secondary-600"></i>
                        </div>
                        <p className="text-foreground-800 font-medium">感谢您的留言！</p>
                        <p className="text-sm text-foreground-500 mt-1">我们会尽快回复您</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-4">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="hp-field"
                        />
                        <div>
                          <label htmlFor="about-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                          <input
                            id="about-name"
                            name="name"
                            type="text"
                            required
                            className="w-full h-10 px-4 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            id="about-email"
                            name="email"
                            type="email"
                            required
                            className="w-full h-10 px-4 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                          <textarea
                            id="about-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入您的留言，我们会尽快回复（限500字）"
                          ></textarea>
                        </div>
                        {formStatus === 'error' && (
                          <p className="text-xs text-accent-600">{formError}</p>
                        )}
                        <button
                          type="submit"
                          className="w-full h-11 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                        >
                          提交留言
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                  常见问题
                </h2>
                <div className="space-y-4">
                  {[
                    { q: '51网址导航是正规平台吗？', a: '是的，51网址导航是正规的上网导航平台，致力于为用户提供安全、便捷的网址导航服务。我们对接入网站进行严格审核，保障用户上网安全。' },
                    { q: '网址导航123和123导航有什么区别？', a: '网址导航123和123导航都是51网址导航提供的数字标识导航服务。"123"代表简单、易记的导航理念，让用户只需记住简洁的数字标识即可快速访问各类网站。' },
                    { q: '我可以推荐网站加入导航吗？', a: '当然可以！如果您有优秀的网站推荐，欢迎通过联系表单或邮箱提交给我们。我们的编辑团队会进行审核，通过后即可加入导航列表。' },
                    { q: '51网址导航收费吗？', a: '不收费！51网址导航完全免费使用。我们通过提供优质的导航服务和内容来服务广大网民，不向用户收取任何费用。' },
                  ].map((faq, idx) => (
                    <details
                      key={idx}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                      </summary>
                      <div className="px-6 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}